/**
 * Keeps the Courier inbox realtime socket alive so the notification bell
 * updates live without a page reload.
 *
 * Why this exists: the Courier WebSocket (`courier-js`) reconnects with
 * backoff but PERMANENTLY gives up after `MAX_RETRY_ATTEMPTS` (its
 * `retryAttempt` only resets on a successful reconnect). `courier-react`
 * calls `listenForUpdates()` exactly once, at sign-in, and never again — so
 * after a laptop sleep, tab backgrounding, network blip, or server-requested
 * reconnect, the socket stays dead and new inbox messages never arrive until a
 * full page reload re-signs-in and reconnects. This wires the missing
 * reconnection triggers:
 *
 *   - `visibilitychange` → visible: the user returned to the tab. `resync`
 *     (refetch + re-listen) so messages delivered while the socket was dead
 *     show up immediately — this is what a manual reload was doing.
 *   - `online`: the network came back. Same `resync`.
 *   - a periodic backstop that re-asserts the socket is listening, to catch
 *     "silent death" while the tab stayed in the foreground.
 *
 * `listenForUpdates()` is idempotent — it no-ops when the socket is already
 * open and reconnects when it isn't — so re-asserting is cheap and safe.
 */

export interface InboxRealtimeKeepaliveCallbacks {
  /**
   * Refetch the inbox, then re-establish the realtime listener. Used on the
   * "returned to the app" signals (tab focus / network online) so messages
   * that arrived while the socket was dead are backfilled, not just future
   * ones.
   */
  resync: () => void;
  /**
   * Called by the periodic backstop. Usually a cheap socket-liveness
   * re-assert (no-op when the socket is already open); the caller MAY instead
   * run a full refetch when it knows nothing has loaded successfully yet — a
   * bare re-listen can't repair a feed that was never fetched in the first
   * place (see CourierInboxBridge).
   */
  ensureListening: () => void;
}

/** How often the foreground backstop re-asserts the socket is listening. */
export const INBOX_REALTIME_RESYNC_POLL_MS = 30_000;

/**
 * Wires the reconnection triggers and returns a cleanup function that removes
 * every listener and clears the interval. Call from a client effect while the
 * user is signed in; invoke the returned cleanup on sign-out / unmount.
 */
export function startInboxRealtimeKeepalive(
  callbacks: InboxRealtimeKeepaliveCallbacks,
  pollMs: number = INBOX_REALTIME_RESYNC_POLL_MS,
): () => void {
  const { resync, ensureListening } = callbacks;

  const onVisible = () => {
    if (document.visibilityState === "visible") resync();
  };

  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", resync);
  const intervalId = window.setInterval(ensureListening, pollMs);

  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", resync);
    window.clearInterval(intervalId);
  };
}
